import { useState } from "react";

import useAccountMetadata from "@/hooks/useAccountMetadata";
import { RoleAdmin } from "@/components/users/inviteUser";
import { User } from "@/components/users/types";
import { UserEnvironmentSelectorBase } from "./UserEnvironmentSelectorBase";

type UserEnvsMap = Record<string, Record<string, Record<string, boolean>>>;

const overrideKey = (projID: string, envID: string) => `${projID}::${envID}`;

type UserEnvironmentSelectorUsersProps = {
  member: User;
  loadingUserEnvs: boolean;
  userEnvs: UserEnvsMap | null;
  enableToggle: boolean;
  onUserEnvAccessUpdate: (
    uid: string,
    projID: string | null,
    envID: string | null,
    hasAccess: boolean,
  ) => void;
};

export const UserEnvironmentSelectorUsersTable = ({
  member,
  loadingUserEnvs,
  userEnvs,
  enableToggle,
  onUserEnvAccessUpdate,
}: UserEnvironmentSelectorUsersProps) => {
  const accountMetadata = useAccountMetadata();

  const [overrides, setOverrides] = useState<Record<string, boolean>>({});

  const isAdmin = member.role === RoleAdmin;
  if (!isAdmin && loadingUserEnvs) {
    return <>Loading</>;
  }

  const getHasAccess = (projID: string, envID: string) => {
    if (isAdmin) {
      return true;
    }
    const key = overrideKey(projID, envID);
    if (key in overrides) {
      return overrides[key];
    }
    return !!userEnvs?.[member.uid]?.[projID]?.[envID];
  };

  const affectedLeaves = (
    projID: string | null,
    envID: string | null,
  ): Array<[string, string]> => {
    if (projID && envID) {
      return [[projID, envID]];
    }
    const leaves: Array<[string, string]> = [];
    for (const project of accountMetadata.projects) {
      if (project.ID === null) continue;
      if (projID && project.ID !== projID) continue;
      for (const env of project.environments) {
        if (env.ID !== null) {
          leaves.push([project.ID, env.ID]);
        }
      }
    }
    return leaves;
  };

  const updateAccess = (
    projID: string | null,
    envID: string | null,
    hasAccess: boolean,
  ) => {
    const leaves = affectedLeaves(projID, envID);
    setOverrides((prev) => {
      const next = { ...prev };
      for (const [proj, env] of leaves) {
        next[overrideKey(proj, env)] = hasAccess;
      }
      return next;
    });
    onUserEnvAccessUpdate(member.uid, projID, envID, hasAccess);
  };

  return (
    <UserEnvironmentSelectorBase
      getHasAccess={getHasAccess}
      updateAccess={updateAccess}
      enableToggle={!isAdmin && enableToggle}
      showAllProjectsEnvs={enableToggle && accountMetadata.projects.length > 0}
      showProjectCheckbox={enableToggle}
    />
  );
};
